import React, { createContext, useContext, useReducer, ReactNode, useCallback } from 'react';
import { AuthState, User, PostMessageData, BackendUser, AdminTokenData, AdminLoginResponse, ConnectAccountRequest, ConnectAccountResponse, AuthPlatform, StoredTokens } from '../types';
import { useApiCall, ApiError, SessionExpiredError } from '../hooks/useApiCall';
import { isMobileUserAgent } from '../utils/mobileUtils';
import { authLogger } from '../utils/logger';
import { getServiceHeaders, getServiceName } from '../utils/serviceUtils';
import { normalizeReturnUrl } from '../utils/returnUrl';
import { upsertVkAccount } from '../utils/vkAccounts';
import { isOffline, sessionGet, sessionRemove, storageGet, storageRemove, storageSet } from '../utils/storage';
import { apiUrl } from '../utils/api';
import { decodeJwtPayload, isRefreshDue, isRefreshableToken, refreshedAtKey } from '../utils/tokenRefresh';

const USER_ENDPOINT = apiUrl('/user');

/** Не ждём бэк дольше: токен сервису отдаём в любом случае. */
const SERVICE_ENTRY_TIMEOUT_MS = 3000;

/** Один запрос на пару «аккаунт + сервис»: login() и выдача токена сервису ждут один и тот же промис. */
const serviceEntries = new Map<string, Promise<void>>();

/**
 * Отмечает вход в сервис (users.services) на бэке. Нужно для уже вошедшего пользователя: логина нет,
 * а токен сервису уходит — ни один запрос входа с service в этом случае не делается.
 * Токен с act (работа в аккаунте владельца связи) отмечаем как обычный. Админские, временные и сервисные — нет (бэк отсекает так же).
 * Никогда не бросает.
 */
const markServiceEntry = (token: string): Promise<void> => {
  const service = getServiceName();
  if (!service) return Promise.resolve();

  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.uid !== 'string' || !payload.uid) return Promise.resolve();
  // act-токены всегда со сроком, обычный токен со сроком — временный.
  if (payload.issuedBy || payload.type === 'service' || (payload.exp !== undefined && !payload.act)) return Promise.resolve();

  const key = `${payload.uid}:${service}`;
  const existing = serviceEntries.get(key);
  if (existing) return existing;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SERVICE_ENTRY_TIMEOUT_MS);
  const request = fetch(apiUrl('/auth/service-entry'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...getServiceHeaders() },
    body: '{}',
    signal: controller.signal,
    keepalive: true,
  })
    .then((response) => {
      // Отказ или сбой — в следующий раз попробуем снова.
      if (!response.ok) serviceEntries.delete(key);
    })
    .catch(() => {
      serviceEntries.delete(key);
    })
    .finally(() => clearTimeout(timer));

  serviceEntries.set(key, request);
  return request;
};

export const TOKEN_KEYS = {
  vk: 'ssmm-token-vk',
  telegram: 'ssmm-token-telegram',
  max: 'ssmm-token-max',
  yandex: 'ssmm-token-yandex',
  email: 'ssmm-token-email',
} as const;
export const CACHE_KEYS = {
  vk: 'ssmm-vk-user-cache',
  telegram: 'ssmm-telegram-user-cache',
  max: 'ssmm-max-user-cache',
  yandex: 'ssmm-yandex-user-cache',
  email: 'ssmm-email-user-cache',
} as const;

/** Профиль из ответа бэка. null — обязательного поля платформы нет, значит ответ не про эту сессию. */
const PLATFORM_USERS: Record<AuthPlatform, (backendUser: BackendUser) => User | null> = {
  vk: (b) => (b.vkId ? {
    id: b.vkId,
    name: b.name && b.surname ? `${b.name} ${b.surname}` : b.name || 'VK User',
    avatar: b.photo || '',
    platform: 'vk',
  } : null),
  telegram: (b) => (b.uid ? {
    id: b.uid,
    name: b.name || 'Telegram User',
    avatar: b.photo || '',
    platform: 'telegram',
  } : null),
  max: (b) => (b.uid ? {
    id: b.connection?.max || b.uid,
    name: b.name && b.surname ? `${b.name} ${b.surname}` : b.name || 'MAX User',
    avatar: b.photo || '',
    platform: 'max',
  } : null),
  yandex: (b) => (b.uid ? {
    id: b.connection?.yandex || b.uid,
    name: b.name && b.surname ? `${b.name} ${b.surname}` : b.name || 'Яндекс ID',
    avatar: b.photo || '',
    platform: 'yandex',
  } : null),
  // У входа по почте профиля может не быть вовсе (свежая регистрация) — тогда имя = сам адрес.
  email: (b) => (b.uid ? {
    id: b.email || b.uid,
    name: b.name && b.surname ? `${b.name} ${b.surname}` : b.name || b.email || 'Почта',
    avatar: b.photo || '',
    platform: 'email',
  } : null),
};

const PLATFORMS = Object.keys(PLATFORM_USERS) as AuthPlatform[];

/** Любой сохранённый токен — для запросов, которым важен факт входа, а не платформа. */
export const findAnyStoredToken = (): string | null => {
  for (const platform of PLATFORMS) {
    const token = storageGet(TOKEN_KEYS[platform]);
    if (token) return token;
  }
  return null;
};

/** Сохранённые данные пользователя (показываем, пока бэк не подтвердил/не опроверг токен). */
const readUserCache = (platform: AuthPlatform): { user: User; backendUser: BackendUser } | null => {
  const raw = storageGet(CACHE_KEYS[platform]);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed?.user || !parsed?.backendUser) return null;
    return parsed;
  } catch {
    return null;
  }
};

interface AuthContextType {
  state: AuthState;
  login: (user: User, token: string, backendUser?: BackendUser) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  sendToParent: (data: PostMessageData) => void;
  redirectToReturnUrl: (token: string, customReturnUrl?: string) => void;
  checkStoredTokens: () => Promise<StoredTokens>;
  clearStoredData: (platform?: AuthPlatform) => void;
  adminLogin: (vkIdOrUid: string) => Promise<AdminTokenData>;
  getAdminTokens: () => AdminTokenData[];
  clearAdminToken: (token: string) => void;
  clearAllAdminTokens: () => void;
  setAdminTokenFavorite: (token: string, favorite: boolean) => AdminTokenData[];
  refreshAdminToken: (tokenData: AdminTokenData) => Promise<AdminTokenData>;
  connectAccount: (provider: string, connectionToken: string, primaryToken?: string | null) => Promise<ConnectAccountResponse>;
}

type AuthAction =
  | { type: 'LOGIN'; payload: { user: User; token: string } }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null };

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  isLoading: false,
  error: null,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        isLoading: false,
        error: null,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        isLoading: false,
        error: null,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };
    default:
      return state;
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const { execute } = useApiCall();

  const login = useCallback((user: User, token: string, backendUser?: BackendUser) => {
    authLogger.info('Начало процесса входа', {
      platform: user.platform,
      userId: user.id,
      userName: user.name,
      hasBackendUser: !!backendUser
    });

    // Сохраняем токен в localStorage в зависимости от платформы
    const tokenKey = TOKEN_KEYS[user.platform];
    const persisted = storageSet(tokenKey, token);
    if (persisted) {
      authLogger.success(`Токен сохранен в localStorage`, { tokenKey });
    } else {
      // Приватный режим Safari / запрет хранилища: вход сработает разово, но не переживёт перезагрузку.
      authLogger.error('Браузер не дал сохранить токен — сессия не переживёт перезагрузку', { tokenKey });
    }

    // Сохраняем кэш данных пользователя для быстрого входа
    if (backendUser) {
      const cacheKey = CACHE_KEYS[user.platform];
      storageSet(cacheKey, JSON.stringify({
        user,
        backendUser
      }));
      authLogger.info('Кэш пользователя сохранен', { cacheKey });

      // VK: запоминаем аккаунт в общий список для быстрого переключения/добавления.
      if (user.platform === 'vk') {
        upsertVkAccount(token, user, backendUser);
      }
    }

    // Вход в SSO мог быть без логина (сохранённая сессия, смена аккаунта) — вход в сервис всё равно отмечаем.
    void markServiceEntry(token);

    dispatch({ type: 'LOGIN', payload: { user, token } });
    authLogger.success('Пользователь успешно авторизован');
  }, []);

  const logout = useCallback(() => {
    // Удаляем все токены из localStorage
    PLATFORMS.forEach((platform) => storageRemove(TOKEN_KEYS[platform]));
    // Удаляем старый токен для совместимости
    storageRemove('ssmm-token');
    dispatch({ type: 'LOGOUT' });
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, []);

  const deliverToParent = useCallback((data: PostMessageData) => {
    const hasOpener = !!window.opener;
    const hasParent = window.parent !== window;

    authLogger.info('Попытка отправки данных родительскому окну', {
      dataType: data.type,
      hasOpener,
      hasParent,
      origin: window.location.origin,
      referrer: document.referrer,
      isSecureContext: window.isSecureContext,
    });

    let sentSuccessfully = false;

    // Отправляем данные в родительское окно (opener - для popup)
    if (hasOpener) {
      try {
        // Проверяем, что opener ещё существует (не был закрыт)
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(data, '*');
          authLogger.success('Данные отправлены через window.opener');
          sentSuccessfully = true;
        } else {
          authLogger.warning('window.opener был закрыт');
        }
      } catch (error: any) {
        authLogger.error('Ошибка отправки через window.opener', {
          error: error.message,
          errorName: error.name,
        });
      }
    }

    // Отправляем в parent для случая iframe
    if (hasParent) {
      try {
        window.parent.postMessage(data, '*');
        authLogger.success('Данные отправлены через window.parent');
        sentSuccessfully = true;
      } catch (error: any) {
        authLogger.error('Ошибка отправки через window.parent', {
          error: error.message,
          errorName: error.name,
        });
      }
    }

    // Если ни opener, ни parent не доступны - логируем и пробуем альтернативы
    if (!sentSuccessfully) {
      authLogger.error('Нет доступных каналов для отправки данных родительскому окну', {
        suggestion: 'Страница была открыта напрямую, а не через popup/iframe. Используйте return_url для редиректа.',
        dataType: data.type,
      });

      // Если это успешная авторизация и есть return_url - пробуем редирект
      if (data.type === 'AUTH_SUCCESS' && data.data?.token) {
        const returnUrl = normalizeReturnUrl(
          new URLSearchParams(window.location.search).get('return_url')
            || sessionGet('ssmm-return-url'),
        );

        if (returnUrl) {
          authLogger.info('Fallback: пробуем редирект на return_url', { returnUrl });
          const separator = returnUrl.includes('?') ? '&' : '?';
          const redirectUrl = `${returnUrl}${separator}token=${encodeURIComponent(data.data.token)}`;

          try {
            window.location.href = redirectUrl;
          } catch (redirectError: any) {
            authLogger.error('Ошибка редиректа на return_url', { error: redirectError.message });
          }
        }
      }
    }

    // Пробуем отправить через BroadcastChannel как дополнительный канал
    try {
      if ('BroadcastChannel' in window) {
        const channel = new BroadcastChannel('ssmm-auth');
        channel.postMessage(data);
        channel.close();
        authLogger.info('Данные также отправлены через BroadcastChannel');
      }
    } catch (bcError: any) {
      // BroadcastChannel не критичен, просто логируем
      authLogger.info('BroadcastChannel недоступен', { error: bcError.message });
    }
  }, []);

  // Токен уходит сервису только после отметки входа: popup закрывается сразу по получению сообщения.
  const sendToParent = useCallback((data: PostMessageData) => {
    const token = data.type === 'AUTH_SUCCESS' ? data.data?.token : undefined;
    if (!token) {
      deliverToParent(data);
      return;
    }
    void markServiceEntry(token).then(() => deliverToParent(data));
  }, [deliverToParent]);

  const addExtensionTokenToUrl = useCallback((token: string) => {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');

    authLogger.info('Проверка режима extension', { mode, hasToken: !!token });

    if (mode === 'extension') {
      // Добавляем extensionToken в URL
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('extensionToken', token);

      authLogger.success('Добавление extensionToken в URL', {
        token: token.substring(0, 20) + '...',
        url: currentUrl.toString()
      });

      // Обновляем URL без перезагрузки страницы
      try {
        window.history.replaceState({}, '', currentUrl.toString());
        authLogger.success('URL успешно обновлен с extensionToken');
      } catch (error: any) {
        authLogger.error('Ошибка при обновлении URL', {
          error: error.message
        });
      }

      return true;
    }

    return false;
  }, []);

  const redirectNow = useCallback((token: string, customReturnUrl?: string) => {
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = normalizeReturnUrl(
      customReturnUrl || urlParams.get('return_url') || sessionGet('ssmm-return-url'),
    );
    const isMobile = isMobileUserAgent();
    const mode = urlParams.get('mode');

    authLogger.info('Попытка редиректа на return_url', {
      returnUrl,
      isMobile,
      hasToken: !!token,
      customReturnUrl,
      mode
    });

    // Если режим extension, добавляем токен в URL и не делаем редирект
    if (mode === 'extension') {
      addExtensionTokenToUrl(token);
      return;
    }

    if (returnUrl && isMobile) {
      // Очищаем return_url из sessionStorage после использования
      sessionRemove('ssmm-return-url');

      // Создаем URL с токеном
      const separator = returnUrl.includes('?') ? '&' : '?';
      const redirectUrl = `${returnUrl}${separator}token=${encodeURIComponent(token)}`;

      authLogger.success('Выполнение редиректа', { redirectUrl });

      // Делаем redirect
      try {
        window.location.href = redirectUrl;
      } catch (error: any) {
        authLogger.error('Ошибка при редиректе', {
          error: error.message,
          redirectUrl
        });
      }
    } else {
      if (!returnUrl) {
        authLogger.warning('return_url не найден, редирект не выполнен');
      }
      if (!isMobile) {
        authLogger.info('Устройство не определено как мобильное, редирект пропущен');
      }
    }
  }, [addExtensionTokenToUrl]);

  const redirectToReturnUrl = useCallback((token: string, customReturnUrl?: string) => {
    void markServiceEntry(token).then(() => redirectNow(token, customReturnUrl));
  }, [redirectNow]);

  const clearStoredData = useCallback((platform?: AuthPlatform) => {
    if (platform) {
      // Удаляем токен конкретной платформы
      storageRemove(TOKEN_KEYS[platform]);
      storageRemove(CACHE_KEYS[platform]);
    } else {
      // Удаляем все токены и кэши
      PLATFORMS.forEach((p) => {
        storageRemove(TOKEN_KEYS[p]);
        storageRemove(CACHE_KEYS[p]);
      });
      storageRemove('ssmm-token');
    }
  }, []);

  /**
   * Проверка токена на бэке. Бросает SessionExpiredError (сессия мертва),
   * NetworkError (связи нет) или ApiError (отказ сервера).
   * Повторы: iOS Safari рвёт незавершённые запросы при уходе вкладки в фон
   * (в т.ч. на прыжке в приложение ВК и обратно) — один сорванный запрос
   * не должен приводить к выходу из аккаунта.
   */
  const fetchUserByToken = useCallback(async (token: string): Promise<BackendUser | null> => {
    return await execute({
      url: USER_ENDPOINT,
      method: 'GET',
      headers: {
        'ssmm-token': token,
      },
      retries: 2,
      retryDelayMs: 600,
      // Короче общего дефолта: индикатор «проверяем сессии» не должен висеть минуту,
      // а неответивший бэк всё равно трактуется как «токен живой, но непроверенный».
      timeoutMs: 10000,
    });
  }, [execute]);

  /**
   * Обновление токена платформы (POST /auth/refresh): в токен попадают свежие role/scope.
   * Best effort: любой сбой молча игнорируем и остаёмся на старом токене. Удалять сессию по ответу
   * рефреша нельзя — это можно делать только по протухшей сессии (код 2003), как и везде в приложении.
   * Возвращает токен, которым надо пользоваться дальше.
   */
  const refreshStoredToken = useCallback(async (platform: AuthPlatform, token: string, uid?: string): Promise<string> => {
    if (!isRefreshableToken(token)) return token;
    if (!isRefreshDue(storageGet(refreshedAtKey(platform)), Date.now())) return token;

    try {
      const response = await execute({
        url: apiUrl('/auth/refresh'),
        method: 'POST',
        headers: { 'ssmm-token': token, ...getServiceHeaders() },
        // Fastify отклоняет POST с Content-Type: application/json и пустым телом.
        data: {},
        timeoutMs: 10000,
      });
      const fresh = response?.token;
      if (typeof fresh !== 'string' || !fresh || (uid && response?.uid !== uid)) {
        authLogger.warning(`${platform}: рефреш вернул неожиданный ответ — остаёмся на старом токене`);
        return token;
      }
      // Пока шёл запрос, в хранилище мог оказаться другой токен (вход в другой аккаунт) — его не затираем.
      if (storageGet(TOKEN_KEYS[platform]) !== token) return token;

      storageSet(TOKEN_KEYS[platform], fresh);
      storageSet(refreshedAtKey(platform), String(Date.now()));
      authLogger.success(`${platform}: токен обновлён`);
      return fresh;
    } catch (error: any) {
      // Отказ сервера (4xx) повторять при каждом старте незачем; сетевой сбой — попробуем в следующий раз.
      if (error instanceof ApiError) storageSet(refreshedAtKey(platform), String(Date.now()));
      authLogger.warning(`${platform}: не удалось обновить токен, остаёмся на старом`, { error: error?.message });
      return token;
    }
  }, [execute]);

  const checkStoredTokens = useCallback(async (): Promise<StoredTokens> => {
    const result: StoredTokens = {};

    try {
      setLoading(true);
      setError(null);

      // Без сети идти на бэк бессмысленно: показываем кэш, токены оставляем как есть.
      const offline = isOffline();
      if (offline) {
        authLogger.warning('Нет сети — сессии показываем из кэша, токены не трогаем');
      }

      // Проверяем сохранённые сессии: VK, Telegram, Яндекс ID — правила одни на всех.
      for (const platform of PLATFORMS) {
        const token = storageGet(TOKEN_KEYS[platform]);
        if (!token) continue;

        const cached = readUserCache(platform);

        if (offline) {
          if (cached) result[platform] = { ...cached, isExpired: false, isUnverified: true };
          continue;
        }

        try {
          const userResponse = await fetchUserByToken(token);
          const user = userResponse ? PLATFORM_USERS[platform](userResponse) : null;

          if (userResponse && user) {
            result[platform] = { user, backendUser: userResponse, isExpired: false };
            await refreshStoredToken(platform, token, userResponse.uid);
          } else {
            // 200, но без опорного поля — это не «сессия истекла», а неожиданный ответ.
            // Токен НЕ удаляем, показываем кэш как непроверенный.
            authLogger.warning(`${platform}: неожиданный ответ бэка — токен оставляем, показываем кэш`);
            if (cached) result[platform] = { ...cached, isExpired: false, isUnverified: true };
          }
        } catch (error: any) {
          console.error(`Error checking ${platform} token:`, error);

          if (error instanceof SessionExpiredError) {
            // Сессия действительно мертва (код 2003) — единственный повод удалять токен.
            if (cached) {
              result[platform] = { ...cached, isExpired: true };
            } else {
              storageRemove(TOKEN_KEYS[platform]);
              storageRemove(CACHE_KEYS[platform]);
            }
          } else {
            // Сеть/сервер/непонятный отказ — токен живой до доказательства обратного.
            authLogger.warning(`${platform}: не удалось проверить токен, сессию сохраняем`, { error: error?.message });
            if (cached) result[platform] = { ...cached, isExpired: false, isUnverified: true };
          }
        }
      }

      // Проверяем старый токен для совместимости
      const oldToken = storageGet('ssmm-token');
      if (oldToken && !result.vk && !result.telegram && !result.max && !result.yandex && !offline) {
        try {
          const userResponse = await fetchUserByToken(oldToken);

          if (userResponse) {
            const platform = userResponse.vkId ? 'vk' : 'telegram';
            const user: User = {
              id: userResponse.vkId || userResponse.uid || 'unknown',
              name: userResponse.name && userResponse.surname
                ? `${userResponse.name} ${userResponse.surname}`
                : userResponse.name || 'User',
              avatar: userResponse.photo || '',
              platform,
            };

            // Переносим старый токен в новый формат
            storageSet(TOKEN_KEYS[platform], oldToken);
            storageRemove('ssmm-token');

            // Сохраняем кэш пользователя
            storageSet(CACHE_KEYS[platform], JSON.stringify({ user, backendUser: userResponse }));

            if (platform === 'vk') {
              result.vk = { user, backendUser: userResponse, isExpired: false };
            } else {
              result.telegram = { user, backendUser: userResponse, isExpired: false };
            }
          }
          // Пустой ответ — токен оставляем: удалять сессию можно только по 2003.
        } catch (error) {
          console.error('Error checking old token:', error);
          // Удаляем старый токен только если он точно невалиден (истёк).
          // При недоступности бэка оставляем — сессия может быть жива.
          if (error instanceof SessionExpiredError) {
            storageRemove('ssmm-token');
          }
        }
      }

      // Сохраняем кэш данных пользователей для случаев истечения токена.
      // Непроверенные (бэк недоступен) данные и так пришли из кэша — не перезаписываем.
      PLATFORMS.forEach((platform) => {
        const status = result[platform];
        if (!status || status.isExpired || status.isUnverified) return;

        storageSet(CACHE_KEYS[platform], JSON.stringify({
          user: status.user,
          backendUser: status.backendUser,
        }));

        // Подхватываем активный VK в общий список (для уже залогиненных пользователей).
        // Токен читаем заново: рефреш мог его заменить.
        const vkToken = platform === 'vk' ? storageGet(TOKEN_KEYS.vk) : null;
        if (platform === 'vk' && vkToken) {
          upsertVkAccount(vkToken, status.user, status.backendUser);
        }
      });

      return result;
    } catch (error) {
      console.error('Error checking stored tokens:', error);
      setError('Ошибка проверки токенов');
      return result;
    } finally {
      setLoading(false);
    }
  }, [fetchUserByToken, refreshStoredToken, setError, setLoading]);

  const adminLogin = useCallback(async (vkIdOrUid: string): Promise<AdminTokenData> => {
    try {
      setLoading(true);
      setError(null);

      // Получаем текущий токен для авторизации админского запроса
      const currentToken = state.token || findAnyStoredToken();

      if (!currentToken) {
        throw new Error('Необходимо войти в систему для использования админских функций');
      }

      // Делаем запрос на админский логин
      const response: AdminLoginResponse = await execute({
        url: apiUrl('/auth/admin/login'),
        method: 'POST',
        headers: {
          'ssmm-token': currentToken,
          'Authorization': `Bearer ${currentToken}`,
          ...getServiceHeaders(),
        },
        data: {
          any: vkIdOrUid,
        },
      });

      if (!response?.token) {
        throw new Error('Не получен токен от сервера');
      }

      // Получаем информацию о пользователе с новым токеном
      const userResponse: BackendUser = await execute({
        url: USER_ENDPOINT,
        method: 'GET',
        headers: {
          'ssmm-token': response.token,
        },
      });

      // Получаем информацию об оригинальном админе
      let originalUser: BackendUser | undefined;
      try {
        originalUser = await execute({
          url: USER_ENDPOINT,
          method: 'GET',
          headers: {
            'ssmm-token': currentToken,
          },
        });
      } catch (error) {
        console.warn('Could not fetch original user info:', error);
      }

      const adminTokenData: AdminTokenData = {
        token: response.token,
        user: userResponse,
        loginTime: Date.now(),
        originalUser,
      };

      // Сохраняем админский токен в localStorage
      const existingAdminTokens = getAdminTokens();
      const updatedTokens = [...existingAdminTokens, adminTokenData];
      storageSet('ssmm-admin-tokens', JSON.stringify(updatedTokens));

      return adminTokenData;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error.message || 'Ошибка админской авторизации';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [execute, state.token]);

  const getAdminTokens = useCallback((): AdminTokenData[] => {
    try {
      const stored = storageGet('ssmm-admin-tokens');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error parsing admin tokens:', error);
      return [];
    }
  }, []);

  const clearAdminToken = useCallback((tokenToRemove: string) => {
    const existingTokens = getAdminTokens();
    const filteredTokens = existingTokens.filter(tokenData => tokenData.token !== tokenToRemove);
    storageSet('ssmm-admin-tokens', JSON.stringify(filteredTokens));
  }, [getAdminTokens]);

  const clearAllAdminTokens = useCallback(() => {
    storageRemove('ssmm-admin-tokens');
  }, []);

  const setAdminTokenFavorite = useCallback((token: string, favorite: boolean): AdminTokenData[] => {
    const updated = getAdminTokens().map((t) =>
      t.token === token ? { ...t, favorite } : t,
    );
    storageSet('ssmm-admin-tokens', JSON.stringify(updated));
    return updated;
  }, [getAdminTokens]);

  // Обновляет токен (повторный админ-вход по тому же пользователю) и заменяет им старый,
  // перенося отметку «избранное». Используется и кнопкой обновления, и «Использовать».
  const refreshAdminToken = useCallback(async (tokenData: AdminTokenData): Promise<AdminTokenData> => {
    const userId = tokenData.user.vkId || tokenData.user.uid;
    if (!userId) {
      throw new Error('ID не найден');
    }

    const fresh = await adminLogin(String(userId));
    clearAdminToken(tokenData.token);

    if (tokenData.favorite) {
      setAdminTokenFavorite(fresh.token, true);
      return { ...fresh, favorite: true };
    }
    return fresh;
  }, [adminLogin, clearAdminToken, setAdminTokenFavorite]);

  const connectAccount = useCallback(async (
    provider: string,
    connectionToken: string,
    /**
     * Токен аккаунта, к которому цепляем. В связывании его выбирает пользователь
     * («главный аккаунт»), и подставлять вместо него что попало нельзя: фолбэк
     * findAnyStoredToken идёт по PLATFORMS и всегда отдаёт ВК, если ВК-сессия есть,
     * поэтому связывание уезжало не к тому профилю.
     */
    primaryToken?: string | null,
  ): Promise<ConnectAccountResponse> => {
    try {
      setLoading(true);
      setError(null);

      // Получаем токен текущей сессии (любой платформы)
      const currentToken = primaryToken || state.token || findAnyStoredToken();

      if (!currentToken) {
        throw new Error('Необходимо войти в систему для подключения аккаунта');
      }

      const response: ConnectAccountResponse = await execute({
        url: apiUrl(`/oauth/${provider}/connect`),
        method: 'POST',
        headers: {
          'ssmm-token': currentToken,
          ...getServiceHeaders(),
        },
        data: {
          connectionToken
        } as ConnectAccountRequest,
      });

      // Проверяем, что ответ не null и имеет правильную структуру
      if (!response || !response.result || response.result.result !== true) {
        throw new Error('Не удалось подключить аккаунт, обратитесь в тех. поддержку');
      }

      return response;
    } catch (error: any) {
      // Не устанавливаем ошибку в контекст, пусть компонент сам её обработает
      throw error; // Пробрасываем оригинальную ошибку
    } finally {
      setLoading(false);
    }
  }, [execute, setLoading, setError, state.token]);

  const value: AuthContextType = {
    state,
    login,
    logout,
    setLoading,
    setError,
    sendToParent,
    redirectToReturnUrl,
    checkStoredTokens,
    clearStoredData,
    adminLogin,
    getAdminTokens,
    clearAdminToken,
    clearAllAdminTokens,
    setAdminTokenFavorite,
    refreshAdminToken,
    connectAccount,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 